import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, Polygon, ZoomControl, useMap, useMapEvents } from "react-leaflet";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import { ExternalLink, MapPin, Circle as CircleIcon, Hexagon, Eraser, Check } from "lucide-react";

const BAND_COLOR = (score) => (score >= 70 ? "#16a34a" : score >= 40 ? "#f59e0b" : "#dc2626");

function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371000, r = Math.PI / 180;
  const dLat = (lat2 - lat1) * r, dLon = (lon2 - lon1) * r;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * r) * Math.cos(lat2 * r) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}
function pointInPolygon(lat, lon, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [yi, xi] = poly[i], [yj, xj] = poly[j];
    const intersect = (xi > lon) !== (xj > lon) && lat < ((yj - yi) * (lon - xi)) / (xj - xi) + yi;
    if (intersect) inside = !inside;
  }
  return inside;
}

const pinIcon = (score, approx, selected) =>
  L.divIcon({
    className: "radar-pin",
    html: `<div style="position:relative;${selected ? "transform:scale(1.35);z-index:1000;" : ""}">
      <div style="width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);
        background:${BAND_COLOR(score)};border:${selected ? "3px solid #0F172A" : "2px solid #fff"};box-shadow:0 2px 6px rgba(0,0,0,.35);
        ${selected ? "box-shadow:0 0 0 4px rgba(198,168,124,.6),0 2px 8px rgba(0,0,0,.5);" : ""}
        ${approx ? "opacity:.7;border-style:dashed;" : ""}"></div>
      <div style="position:absolute;top:4px;left:0;width:26px;text-align:center;color:#fff;font-size:10px;font-weight:700;">${score}</div>
    </div>`,
    iconSize: [26, 26], iconAnchor: [13, 26], popupAnchor: [0, -24],
  });

const clusterIcon = (count, avg) =>
  L.divIcon({
    className: "radar-cluster",
    html: `<div style="width:40px;height:40px;border-radius:50%;background:${BAND_COLOR(avg)};
      border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.4);display:flex;align-items:center;
      justify-content:center;color:#fff;font-weight:800;font-size:14px;">${count}</div>`,
    iconSize: [40, 40], iconAnchor: [20, 20],
  });

function FitBounds({ points, disabled }) {
  const map = useMap();
  useEffect(() => {
    if (disabled) return;
    if (points.length === 1) map.setView([points[0].lat, points[0].lon], 14);
    else if (points.length > 1) map.fitBounds(points.map((p) => [p.lat, p.lon]), { padding: [40, 40], maxZoom: 15 });
  }, [points, map, disabled]);
  return null;
}

// Centre/zoome automatiquement sur le secteur recherché (ville/CP/arrondissement)
function FocusView({ focus }) {
  const map = useMap();
  useEffect(() => {
    if (!focus || typeof focus.lat !== "number") return;
    const r = focus.radius_m || 5000;
    const z = r <= 3500 ? 14 : r <= 8000 ? 13 : 11;
    map.setView([focus.lat, focus.lon], z, { animate: true });
  }, [focus, map]);
  return null;
}

// Capture clics (dessin) + suit le zoom pour recalculer les clusters
function MapInteraction({ drawMode, onClick, onViewChange }) {
  useMapEvents({
    click(e) { if (drawMode) onClick(e.latlng); },
    zoomend() { onViewChange(); },
    moveend() { onViewChange(); },
  });
  return null;
}

function ClusteredMarkers({ points, onOpen, version, selectedId, onHover }) {
  const { t } = useTranslation();
  const map = useMap();
  const clusters = useMemo(() => {
    const cell = 70; // px
    const buckets = {};
    points.forEach((p) => {
      const pt = map.latLngToContainerPoint([p.lat, p.lon]);
      const key = `${Math.floor(pt.x / cell)}_${Math.floor(pt.y / cell)}`;
      (buckets[key] = buckets[key] || []).push(p);
    });
    return Object.values(buckets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, version, map, selectedId]);

  return (
    <>
      {clusters.map((grp, idx) => {
        if (grp.length === 1) {
          const l = grp[0];
          const pm2 = l.surface ? Math.round((l.current_price || 0) / l.surface) : null;
          return (
            <Marker key={l.id} position={[l.lat, l.lon]} icon={pinIcon(l.score, !l.geo_precise, selectedId === l.id)}
              eventHandlers={{ mouseover: () => onHover && onHover(l.id), mouseout: () => onHover && onHover(null) }}>
              <Popup>
                <div style={{ width: 240 }} data-testid={`radar-map-popup-${l.id}`}>
                  {l.photo ? <img src={l.photo} alt="" style={{ width: "100%", height: 110, objectFit: "cover", borderRadius: 6, marginBottom: 6 }} /> : null}
                  <p style={{ margin: "0 0 2px", fontWeight: 700, color: "#0F172A" }}>{l.title}</p>
                  <p style={{ margin: 0, fontSize: 12, color: "#64748b" }}>{[l.city, l.quartier].filter(Boolean).join(" · ") || "—"}</p>
                  {!l.geo_precise && <p style={{ margin: "4px 0 0", fontSize: 11, color: "#b45309", display: "flex", alignItems: "center", gap: 4 }}><MapPin size={12} /> {t("radar.map.approx")}</p>}
                  <div style={{ display: "flex", justifyContent: "space-between", margin: "6px 0", fontSize: 13 }}>
                    <span style={{ fontWeight: 600, color: "#0F172A" }}>{l.current_price ? `${Number(l.current_price).toLocaleString("fr-FR")} €` : t("radar.results.priceNc")}</span>
                    <span style={{ color: "#64748b" }}>{l.surface ? `${l.surface} m²` : ""}{pm2 ? ` · ${pm2.toLocaleString("fr-FR")} €/m²` : ""}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <span style={{ background: BAND_COLOR(l.score), color: "#fff", borderRadius: 999, padding: "2px 8px", fontSize: 12, fontWeight: 700 }}>{l.score}/100</span>
                    <span style={{ fontSize: 12, color: "#475569" }}>{l.band}</span>
                  </div>
                  {l.score_reasons && l.score_reasons.length > 0 && (
                    <ul style={{ margin: "0 0 8px", paddingLeft: 16, fontSize: 11, color: "#475569" }}>
                      {l.score_reasons.slice(0, 3).map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  )}
                  <Button size="sm" onClick={() => { map.closePopup(); onOpen(l.id); }} data-testid={`radar-map-open-${l.id}`} className="w-full rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]">
                    <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> {t("radar.map.openFull")}
                  </Button>
                </div>
              </Popup>
            </Marker>
          );
        }
        const lat = grp.reduce((s, p) => s + p.lat, 0) / grp.length;
        const lon = grp.reduce((s, p) => s + p.lon, 0) / grp.length;
        const avg = Math.round(grp.reduce((s, p) => s + p.score, 0) / grp.length);
        return (
          <Marker key={`cl-${idx}`} position={[lat, lon]} icon={clusterIcon(grp.length, avg)}
            eventHandlers={{ click: () => map.fitBounds(grp.map((p) => [p.lat, p.lon]), { padding: [60, 60], maxZoom: 17 }) }} />
        );
      })}
    </>
  );
}

const COUNTRY_CENTERS = {
  FR: [46.6, 2.4], BE: [50.6, 4.6], NL: [52.2, 5.3], IT: [42.8, 12.5], ES: [40.2, -3.7],
  CA: [56.1, -106.3], US: [39.5, -98.35], AE: [24.3, 54.3], TR: [39.0, 35.2], AU: [-25.3, 133.8],
};
const COUNTRY_ZOOM = { US: 4, CA: 3, AU: 4, AE: 7, TR: 5 };

export default function RadarMap({ listings, onOpen, onZoneCount, country = "FR", focus = null, selectedId = null, onHover = null }) {
  const { t } = useTranslation();
  const [drawMode, setDrawMode] = useState(null); // 'radius' | 'polygon'
  const [circle, setCircle] = useState(null);     // {center:[lat,lon], radius}
  const [radius, setRadius] = useState(1000);
  const [polyPts, setPolyPts] = useState([]);
  const [polyClosed, setPolyClosed] = useState(false);
  const [version, setVersion] = useState(0);

  const points = useMemo(
    () => (listings || [])
      .filter((l) => typeof l.geo_lat === "number" && typeof l.geo_lon === "number")
      .map((l) => ({ ...l, lat: l.geo_lat, lon: l.geo_lon })),
    [listings]
  );

  // Filtre "secteur demandé" : ne garde que les biens autour de la zone recherchée (+ petite marge)
  const focusPoints = useMemo(() => {
    if (!focus || typeof focus.lat !== "number") return points;
    const r = (focus.radius_m || 5000) * 1.2;
    return points.filter((p) => haversine(focus.lat, focus.lon, p.lat, p.lon) <= r);
  }, [points, focus]);

  const zoneActive = !!circle || polyClosed;
  const inZone = useMemo(() => {
    if (circle) return focusPoints.filter((p) => haversine(circle.center[0], circle.center[1], p.lat, p.lon) <= circle.radius);
    if (polyClosed && polyPts.length >= 3) return focusPoints.filter((p) => pointInPolygon(p.lat, p.lon, polyPts));
    return focusPoints;
  }, [focusPoints, circle, polyClosed, polyPts]);

  useEffect(() => { onZoneCount && onZoneCount(zoneActive ? inZone.length : null); }, [zoneActive, inZone, onZoneCount]);

  // Compteurs EXACTS : correspondent aux marqueurs réellement affichés (inZone)
  const counts = useMemo(() => {
    const c = { green: 0, orange: 0, red: 0 };
    inZone.forEach((p) => { const s = p.score || 0; if (s >= 70) c.green++; else if (s >= 40) c.orange++; else c.red++; });
    return { ...c, total: inZone.length };
  }, [inZone]);

  const handleMapClick = (latlng) => {
    if (drawMode === "radius") setCircle({ center: [latlng.lat, latlng.lng], radius });
    else if (drawMode === "polygon") setPolyPts((p) => [...p, [latlng.lat, latlng.lng]]);
  };
  const clearZone = () => { setCircle(null); setPolyPts([]); setPolyClosed(false); setDrawMode(null); };
  const finishPolygon = () => { if (polyPts.length >= 3) { setPolyClosed(true); setDrawMode(null); } };

  useEffect(() => { if (circle) setCircle((c) => c && { ...c, radius }); }, [radius]); // eslint-disable-line react-hooks/exhaustive-deps

  const center = (focus && typeof focus.lat === "number") ? [focus.lat, focus.lon]
    : (points.length ? [points[0].lat, points[0].lon] : (COUNTRY_CENTERS[country] || [46.6, 2.4]));
  const emptyZoom = COUNTRY_ZOOM[country] || 5;
  const initialZoom = (focus && typeof focus.lat === "number")
    ? ((focus.radius_m || 5000) <= 3500 ? 14 : (focus.radius_m || 5000) <= 8000 ? 13 : 11)
    : (points.length ? 12 : emptyZoom);

  return (
    <div data-testid="radar-map">
      {/* Barre d'outils dessin */}
      <div className="mb-2 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-2">
        <span className="pl-1 text-xs text-slate-500">{t("radar.map.zone")}</span>
        <Button size="sm" variant={drawMode === "radius" ? "default" : "outline"} onClick={() => { setDrawMode("radius"); setPolyPts([]); setPolyClosed(false); }} data-testid="radar-draw-radius" className="rounded-full">
          <CircleIcon className="mr-1.5 h-3.5 w-3.5" /> {t("radar.map.radius")}
        </Button>
        <Button size="sm" variant={drawMode === "polygon" ? "default" : "outline"} onClick={() => { setDrawMode("polygon"); setCircle(null); setPolyPts([]); setPolyClosed(false); }} data-testid="radar-draw-polygon" className="rounded-full">
          <Hexagon className="mr-1.5 h-3.5 w-3.5" /> {t("radar.map.polygon")}
        </Button>
        {drawMode === "radius" && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">{(radius / 1000).toFixed(1)} km</span>
            <input type="range" min="200" max="5000" step="100" value={radius} onChange={(e) => setRadius(Number(e.target.value))} data-testid="radar-radius-slider" />
          </div>
        )}
        {drawMode === "polygon" && (
          <Button size="sm" onClick={finishPolygon} disabled={polyPts.length < 3} data-testid="radar-poly-finish" className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700">
            <Check className="mr-1.5 h-3.5 w-3.5" /> {t("radar.map.finish", { n: polyPts.length })}
          </Button>
        )}
        {(zoneActive || polyPts.length > 0) && (
          <Button size="sm" variant="outline" onClick={clearZone} data-testid="radar-zone-clear" className="rounded-full text-red-600">
            <Eraser className="mr-1.5 h-3.5 w-3.5" /> {t("radar.map.clearZone")}
          </Button>
        )}
        {zoneActive && <span className="ml-auto text-xs font-medium text-[#0F172A]" data-testid="radar-zone-count">{t("radar.map.inZone", { n: inZone.length })}</span>}
        {drawMode && <span className="w-full pl-1 text-xs text-[#C6A87C]">{drawMode === "radius" ? t("radar.map.hintRadius") : t("radar.map.hintPolygon")}</span>}
      </div>

      <div className="relative overflow-hidden rounded-2xl border border-slate-200">
        {/* Compteurs — correspondent exactement aux marqueurs affichés */}
        <div className="pointer-events-none absolute left-3 top-3 z-[1000] w-[190px] rounded-xl border border-white/10 bg-[#0B1220]/85 p-2.5 text-white backdrop-blur-sm" data-testid="radar-map-counters">
          <p className="mb-1.5 text-[9px] font-semibold uppercase tracking-wide text-slate-400">{t("radar.map.summary")}</p>
          {[["green", "#16a34a", t("radar.map.greenCount")], ["orange", "#f59e0b", t("radar.map.orangeCount")], ["red", "#dc2626", t("radar.map.redCount")]].map(([k, c, label]) => (
            <div key={k} className="mb-1 flex items-center justify-between rounded-md px-2 py-1" style={{ background: `${c}22` }}>
              <span className="flex items-center gap-1.5 text-[11px] font-medium" style={{ color: c }}><MapPin className="h-3 w-3" /> {label}</span>
              <span className="text-xs font-bold text-white" data-testid={`radar-count-${k}`}>{counts[k]}</span>
            </div>
          ))}
          <div className="mt-1.5 flex items-center justify-between border-t border-white/10 px-2 pt-1.5 text-[11px]">
            <span className="text-slate-400">{t("radar.map.totalLabel")}</span>
            <span className="font-bold text-white" data-testid="radar-count-total">{counts.total}</span>
          </div>
        </div>
        <MapContainer center={center} zoom={initialZoom} scrollWheelZoom zoomControl={false} style={{ height: "560px", width: "100%" }}>
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <ZoomControl position="topright" />
          <FitBounds points={focusPoints} disabled={zoneActive || !!drawMode || !!focus} />
          <FocusView focus={focus} />
          <MapInteraction drawMode={drawMode} onClick={handleMapClick} onViewChange={() => setVersion((v) => v + 1)} />
          {focus && typeof focus.lat === "number" && !zoneActive && (
            <Circle center={[focus.lat, focus.lon]} radius={focus.radius_m || 5000}
              pathOptions={{ color: "#0F172A", weight: 1, fillColor: "#0F172A", fillOpacity: 0.04, dashArray: "6" }} />
          )}
          {circle && <Circle center={circle.center} radius={circle.radius} pathOptions={{ color: "#C6A87C", fillColor: "#C6A87C", fillOpacity: 0.12 }} />}
          {polyPts.length >= 2 && <Polygon positions={polyPts} pathOptions={{ color: "#C6A87C", fillColor: "#C6A87C", fillOpacity: polyClosed ? 0.12 : 0.05, dashArray: polyClosed ? null : "6" }} />}
          <ClusteredMarkers points={inZone} onOpen={onOpen} version={version} selectedId={selectedId} onHover={onHover} />
        </MapContainer>
      </div>
    </div>
  );
}
